import type { AlterregoJwtPayload } from './types';

const TOKEN_KEY = 'alterrego_jwt';
const REFRESH_KEY = 'alterrego_refresh';

/**
 * Stockage du token JWT Alterrego.
 * En natif (Capacitor), utilise @capacitor/preferences (= Keychain/Keystore).
 * En web, utilise sessionStorage (pas localStorage — pas de persistence cross-tab volontaire).
 */

let capacitorPreferences: any = null;

async function getStorage() {
  if (capacitorPreferences) return capacitorPreferences;
  try {
    const mod = await import('@capacitor/preferences');
    capacitorPreferences = mod.Preferences;
    return capacitorPreferences;
  } catch {
    // Fallback web
    return null;
  }
}

export async function storeTokens(jwt: string, refreshToken?: string): Promise<void> {
  const prefs = await getStorage();
  if (prefs) {
    await prefs.set({ key: TOKEN_KEY, value: jwt });
    if (refreshToken) await prefs.set({ key: REFRESH_KEY, value: refreshToken });
  } else {
    sessionStorage.setItem(TOKEN_KEY, jwt);
    if (refreshToken) sessionStorage.setItem(REFRESH_KEY, refreshToken);
  }
}

export async function getToken(): Promise<string | null> {
  const prefs = await getStorage();
  if (prefs) {
    const { value } = await prefs.get({ key: TOKEN_KEY });
    return value;
  }
  return sessionStorage.getItem(TOKEN_KEY);
}

export async function getRefreshToken(): Promise<string | null> {
  const prefs = await getStorage();
  if (prefs) {
    const { value } = await prefs.get({ key: REFRESH_KEY });
    return value;
  }
  return sessionStorage.getItem(REFRESH_KEY);
}

export async function clearTokens(): Promise<void> {
  const prefs = await getStorage();
  if (prefs) {
    await prefs.remove({ key: TOKEN_KEY });
    await prefs.remove({ key: REFRESH_KEY });
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  }
}

/**
 * Décode le payload d'un JWT sans vérification de signature
 * (la vérification est faite côté backend).
 */
export function decodeJwt(token: string): AlterregoJwtPayload {
  const payload = token.split('.')[1];
  return JSON.parse(atob(payload));
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  return Date.now() >= payload.exp * 1000;
}
